import {
  StepLayoutGroupColorSchema,
  type StepLayoutGroupColor,
} from '@erp/workflows';

/**
 * Tailwind classes for the step-layout-group box in each palette family.
 *
 * Deliberately much fainter than the node-header classes in `categoryStyles`:
 * a group sits *behind* a region of nodes across the whole canvas, so at the
 * saturation a 32px header can carry it would drown out its own contents. The
 * tint reads as a wash; the label pill is where the colour is legible.
 */
export interface StepLayoutGroupPaletteEntry {
  /** Human label for the colour picker. */
  name: string;
  /** Fill for the group body. */
  surface: string;
  /** Border for the group body. */
  border: string;
  /** Border when the group node is selected. */
  selectedBorder: string;
  /** The label pill sitting on the group's top edge. */
  label: string;
  /** Solid swatch for the colour picker. */
  swatch: string;
}

export const stepLayoutGroupPalette: Record<
  StepLayoutGroupColor,
  StepLayoutGroupPaletteEntry
> = {
  slate: {
    name: 'Slate',
    surface: 'bg-slate-500/5 dark:bg-slate-400/10',
    border: 'border-slate-300 dark:border-slate-700',
    selectedBorder: 'border-slate-500 dark:border-slate-400',
    label:
      'bg-slate-100 dark:bg-slate-900 text-slate-800 dark:text-slate-100 border-slate-300 dark:border-slate-700',
    swatch: 'bg-slate-400',
  },
  blue: {
    name: 'Blue',
    surface: 'bg-blue-500/5 dark:bg-blue-400/10',
    border: 'border-blue-300 dark:border-blue-800',
    selectedBorder: 'border-blue-500 dark:border-blue-400',
    label:
      'bg-blue-100 dark:bg-blue-900 text-blue-900 dark:text-blue-100 border-blue-300 dark:border-blue-800',
    swatch: 'bg-blue-400',
  },
  violet: {
    name: 'Violet',
    surface: 'bg-violet-500/5 dark:bg-violet-400/10',
    border: 'border-violet-300 dark:border-violet-800',
    selectedBorder: 'border-violet-500 dark:border-violet-400',
    label:
      'bg-violet-100 dark:bg-violet-900 text-violet-900 dark:text-violet-100 border-violet-300 dark:border-violet-800',
    swatch: 'bg-violet-400',
  },
  purple: {
    name: 'Purple',
    surface: 'bg-purple-500/5 dark:bg-purple-400/10',
    border: 'border-purple-300 dark:border-purple-800',
    selectedBorder: 'border-purple-500 dark:border-purple-400',
    label:
      'bg-purple-100 dark:bg-purple-900 text-purple-900 dark:text-purple-100 border-purple-300 dark:border-purple-800',
    swatch: 'bg-purple-400',
  },
  green: {
    name: 'Green',
    surface: 'bg-green-500/5 dark:bg-green-400/10',
    border: 'border-green-300 dark:border-green-800',
    selectedBorder: 'border-green-500 dark:border-green-400',
    label:
      'bg-green-100 dark:bg-green-900 text-green-900 dark:text-green-100 border-green-300 dark:border-green-800',
    swatch: 'bg-green-400',
  },
  orange: {
    name: 'Orange',
    surface: 'bg-orange-500/5 dark:bg-orange-400/10',
    border: 'border-orange-300 dark:border-orange-800',
    selectedBorder: 'border-orange-500 dark:border-orange-400',
    label:
      'bg-orange-100 dark:bg-orange-900 text-orange-900 dark:text-orange-100 border-orange-300 dark:border-orange-800',
    swatch: 'bg-orange-400',
  },
  rose: {
    name: 'Rose',
    surface: 'bg-rose-500/5 dark:bg-rose-400/10',
    border: 'border-rose-300 dark:border-rose-800',
    selectedBorder: 'border-rose-500 dark:border-rose-400',
    label:
      'bg-rose-100 dark:bg-rose-900 text-rose-900 dark:text-rose-100 border-rose-300 dark:border-rose-800',
    swatch: 'bg-rose-400',
  },
  amber: {
    name: 'Amber',
    surface: 'bg-amber-500/5 dark:bg-amber-400/10',
    border: 'border-amber-300 dark:border-amber-800',
    selectedBorder: 'border-amber-500 dark:border-amber-400',
    label:
      'bg-amber-100 dark:bg-amber-900 text-amber-900 dark:text-amber-100 border-amber-300 dark:border-amber-800',
    swatch: 'bg-amber-400',
  },
  cyan: {
    name: 'Cyan',
    surface: 'bg-cyan-500/5 dark:bg-cyan-400/10',
    border: 'border-cyan-300 dark:border-cyan-800',
    selectedBorder: 'border-cyan-500 dark:border-cyan-400',
    label:
      'bg-cyan-100 dark:bg-cyan-900 text-cyan-900 dark:text-cyan-100 border-cyan-300 dark:border-cyan-800',
    swatch: 'bg-cyan-400',
  },
  indigo: {
    name: 'Indigo',
    surface: 'bg-indigo-500/5 dark:bg-indigo-400/10',
    border: 'border-indigo-300 dark:border-indigo-800',
    selectedBorder: 'border-indigo-500 dark:border-indigo-400',
    label:
      'bg-indigo-100 dark:bg-indigo-900 text-indigo-900 dark:text-indigo-100 border-indigo-300 dark:border-indigo-800',
    swatch: 'bg-indigo-400',
  },
  red: {
    name: 'Red',
    surface: 'bg-red-500/5 dark:bg-red-400/10',
    border: 'border-red-300 dark:border-red-800',
    selectedBorder: 'border-red-500 dark:border-red-400',
    label:
      'bg-red-100 dark:bg-red-900 text-red-900 dark:text-red-100 border-red-300 dark:border-red-800',
    swatch: 'bg-red-400',
  },
  teal: {
    name: 'Teal',
    surface: 'bg-teal-500/5 dark:bg-teal-400/10',
    border: 'border-teal-300 dark:border-teal-800',
    selectedBorder: 'border-teal-500 dark:border-teal-400',
    label:
      'bg-teal-100 dark:bg-teal-900 text-teal-900 dark:text-teal-100 border-teal-300 dark:border-teal-800',
    swatch: 'bg-teal-400',
  },
  // Matches the hand-picked tan of the DataSource Writer category, which sits
  // outside Tailwind's named palettes.
  tan: {
    name: 'Tan',
    surface: 'bg-[#e8d5b0]/20 dark:bg-[#3d2a10]/30',
    border: 'border-[#c4a882] dark:border-[#4d3518]',
    selectedBorder: 'border-[#8b6040] dark:border-[#c4a882]',
    label:
      'bg-[#e8d5b0] dark:bg-[#3d2a10] text-[#3d2a10] dark:text-[#e8d5b0] border-[#c4a882] dark:border-[#4d3518]',
    swatch: 'bg-[#c4a882]',
  },
};

/** Palette family used when a group has no colour and its members disagree. */
export const DEFAULT_STEP_LAYOUT_GROUP_COLOR: StepLayoutGroupColor = 'slate';

export const STEP_LAYOUT_GROUP_COLORS: StepLayoutGroupColor[] =
  StepLayoutGroupColorSchema.options;

export function getStepLayoutGroupPalette(
  color: StepLayoutGroupColor | null | undefined,
): StepLayoutGroupPaletteEntry {
  return stepLayoutGroupPalette[color ?? DEFAULT_STEP_LAYOUT_GROUP_COLOR];
}
